import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import CheckmarkChip from '@shared/components/CheckmarkChip'
import FieldLabel from '@shared/forms/FieldLabel'
import NumberField from '@shared/forms/NumberField'
import useRecognitionModal from '@tenant/recognition/RecognitionModal/useRecognitionModal'
import { T } from '@transifex/react'
import DelegatorPointsEstimateDisplay from './DelegatorPointsEstimateDisplay'
import PointsEstimateDisplay from './PointsEstimateDisplay'
import usePointsSelection from './usePointsSelection'

const PointsSelectionField: React.FC = () => {
  const { dialogData } = useRecognitionModal()
  const delegatorId = dialogData?.delegatorId

  const {
    canSendPoints,
    points,
    allowCustomPoints,
    isLoadingPresets,
    amount,
    onAmountChange,
    isUsingCustomAmount,
    disabledValues,
    handleFixedSelection,
    openCustomSelection,
    allocationPoints,
    handleRecipientCountChange,
    handleAllocationPointsChange,
    sendTo,
    control,
    delegatorAccounts,
    isDelegatorPointsFetched,
  } = usePointsSelection({ delegatorId })

  if (!canSendPoints) return null

  if (isLoadingPresets) {
    return (
      <Box
        sx={{
          pb: 2,
        }}>
        <Stack
          useFlexGap
          direction="row"
          spacing={1}
          sx={{
            flexWrap: 'wrap',
          }}>
          {Array.from(new Array(5)).map((_, value) => (
            <Skeleton variant="circular" key={value} width={42} height={36} />
          ))}
        </Stack>
      </Box>
    )
  }

  return (
    <Stack
      useFlexGap
      sx={{
        gap: 1,
      }}>
      <FieldLabel>
        <T _str="Add points" />
      </FieldLabel>
      <Box
        sx={{
          pb: 2,
        }}>
        <Stack
          useFlexGap
          direction="row"
          spacing={1}
          sx={{
            flexWrap: 'wrap',
          }}>
          {points.map(p => (
            <Tooltip
              id={`tooltipCheckmarkChip-${p.id}`}
              key={p.id}
              title={disabledValues?.includes(p.id) ? <T _str="Not enough points" /> : null}
              placement="top">
              <Stack>
                <CheckmarkChip
                  id={`pointsAmountChip-${p.id}`}
                  aria-labelledby={`tooltipCheckmarkChip-${p.id}`}
                  checked={!isUsingCustomAmount && amount === Number(p.id)}
                  label={p.label}
                  value={p.id}
                  disabled={disabledValues?.includes(p.id) ?? false}
                  onChange={value => handleFixedSelection(parseInt(value, 10))}
                />
              </Stack>
            </Tooltip>
          ))}
          {allowCustomPoints && (
            <CheckmarkChip
              id={'pointsAmountChip-other'}
              checked={isUsingCustomAmount}
              label="Other"
              value=""
              disabled={allocationPoints === 0}
              onChange={openCustomSelection}
            />
          )}
        </Stack>
      </Box>
      {isUsingCustomAmount && allowCustomPoints && (
        <Stack
          sx={{
            mb: 1,
          }}>
          <NumberField
            width={150}
            fullWidth
            control={control}
            name="amount"
            label={<T _str="Custom point amount" />}
            labelStyle={{ mt: 0 }}
            decimalScale={0}
            value={amount || ''}
            onChange={event => onAmountChange(parseInt(event.target.value, 10) || 0)}
          />
        </Stack>
      )}
      {delegatorId && delegatorAccounts ? (
        <DelegatorPointsEstimateDisplay
          amount={amount}
          sendTo={sendTo}
          delegatorId={delegatorId}
          onRecipientCountChange={handleRecipientCountChange}
          onAllocationPointsChange={handleAllocationPointsChange}
          delegatorAccounts={delegatorAccounts}
          isDelegatorPointsFetched={isDelegatorPointsFetched}
        />
      ) : (
        <PointsEstimateDisplay
          amount={amount}
          sendTo={sendTo}
          onRecipientCountChange={handleRecipientCountChange}
          onAllocationPointsChange={handleAllocationPointsChange}
        />
      )}
    </Stack>
  )
}

export default PointsSelectionField
